/**
 * LaidOutIR → flat canvas pass.
 *
 * Step 5 of the IR build (see docs/IR_RULE_MAPPING.md §7 step 5).
 *
 * INPUT:  a wrapper produced by `layoutIR()`:
 *           { start, sequence, end, bbox }
 *         where every IR node carries `bbox` in its parent's local coordinate
 *         space (step 4 design).
 *
 * OUTPUT: the render-ready flat structure:
 *           {
 *             nodes: [{ id, type, label, x, y, w, h, config, ... }],
 *             edges: [{ source, target, sourceHandle, style?, isGhost? }],
 *           }
 *         — i.e. the shape CanvasPage.jsx / useCanvas.js already consume
 *         today. Coordinates are ABSOLUTE (document-space), derived by
 *         accumulating parent bboxes during the walk.
 *
 * DESIGN
 * ──────
 *  1. **Single recursive walk.** `emitLaidNode(node, parentAbs, ctx)` takes
 *     the parent's absolute origin, computes the node's own absolute origin
 *     by adding the node's local bbox, emits its flat node(s) and default
 *     out-edges, and returns a `{ entryId, exitId, exitHandle }` triple that
 *     a sequence walker uses to chain the next node.
 *
 *  2. **Absolute coordinates.** Local bboxes + `parentAbs` give absolute
 *     (x, y). Because layout has already dimensioned every container, we
 *     never need to measure or re-layout here.
 *
 *  3. **Containers expand to multiple flat nodes.** A Switch emits its head
 *     + a synthetic convergence pill; a Fork emits its head + the join; a
 *     DoWhile emits just its head (the frame is a visual-only bbox); a
 *     SubWorkflow with `embedded === null` emits a single head.
 *
 *  4. **Chain-linking within a sequence.** `emitSequence(children, parentAbs,
 *     ctx)` walks an array of IR children left-to-right, recording each
 *     child's entry/exit, and emitting a default edge from the previous
 *     child's exit port to the current child's entry. The sequence returns
 *     the first child's entry as its own entry, and the last reachable
 *     child's exit as its own exit. Terminates (and end-typed dead-ends)
 *     break the chain — their exit is `null`, so the sequence's exit stops
 *     at the last non-terminating child.
 *
 *  5. **Ghost edges.** An empty Switch case / Fork branch has no children.
 *     The renderer still wants a dashed edge showing where that empty arm
 *     would go. We synthesize one directly from the container head (for
 *     Switch, with the case's handle) to the container's convergence/join,
 *     tagged `isGhost: true` and `style: 'dashed'`.
 *
 *  6. **Convergence pills.** Every Switch gets a synthesized convergence
 *     node at the bottom of its subtree, with id `<switchId>__conv`,
 *     type = CANVAS_TYPE.CONVERGENCE. Every Switch case (including default)
 *     links its last non-terminating child to this pill with a default edge.
 *     An empty case synthesises a ghost edge from the switch head (handle
 *     = case name) directly to the pill.
 *
 *  7. **Fork join.** Fork / DynamicFork carry a `join` child; we emit it as
 *     a PARALLEL_JOIN node at the join's local bbox position. Each branch's
 *     last non-terminating child edges into the join; empty branches ghost
 *     directly from fork head → join.
 *
 *  8. **Loop edges.** DoWhile head → first body child (default); each body
 *     child chains to the next; the last body child → loop head
 *     (sourceHandle = 'loop_back'). The DoWhile head is the subtree's exit
 *     too — sequence walker continues from it with sourceHandle = 'exit',
 *     so the outer edge the caller emits uses `exitHandle = 'exit'`.
 *
 *  9. **Terminate dead-ends.** A Terminate emits its flat node but returns
 *     `exitId: null` — the chain breaks. A sequence's exit skips past it.
 *
 * NOT IN SCOPE for step 5:
 *   - Port-driven edge routing (step 6 — right now we only emit the logical
 *     `{source, target, sourceHandle}` edge; routing & bendpoints are a
 *     renderer concern).
 *   - Convergence living-branches / pill-vs-label selection (Rule S-E1.c).
 *     We always emit a pill; step 6/7 refine.
 *
 * See: docs/IR_RULE_MAPPING.md §5 (irToFlat), §7 step 5 (build order).
 */

import { IR_KIND, CANVAS_TYPE, irKindToCanvasType } from './kinds';

// ───────────────────────────────────────────────────────────────────
//  Public entry point
// ───────────────────────────────────────────────────────────────────

/**
 * Walk a laid-out IR wrapper and flatten it into a renderable
 * `{ nodes, edges }` structure.
 *
 * The input MUST have been through `layoutIR()` so every node carries
 * `bbox` (parent-local). Trying to flatten a non-laid-out tree will throw.
 *
 * @param {{ start: object, sequence: object[], end: object|null, bbox: {x,y,w,h} }} laid
 * @returns {{ nodes: object[], edges: object[] }}
 */
export function irToFlat(laid) {
  if (!laid || !laid.start || !Array.isArray(laid.sequence) || !laid.bbox) {
    throw new Error('irToFlat: expected a laid-out wrapper { start, sequence, end, bbox }');
  }

  const ctx = {
    nodes: [],
    edges: [],
    emittedIds: new Set(),
  };

  const wrapperAbs = { x: laid.bbox.x, y: laid.bbox.y };

  // 1. Start sentinel
  const startRes = emitLaidNode(laid.start, wrapperAbs, ctx);
  let chainExit = startRes;

  // 2. Top-level sequence: chain from Start → each child → End.
  for (const child of laid.sequence) {
    const childRes = emitLaidNode(child, wrapperAbs, ctx);
    linkChain(ctx, chainExit, childRes);
    // If this child is a dead-end (Terminate or similar), downstream
    // nodes in the sequence cannot be reached. The layout still placed
    // them (they're standalone), so we continue but break the chain:
    // chainExit becomes null, linkChain becomes a no-op, and subsequent
    // nodes are still emitted as flat nodes with no incoming default.
    chainExit = childRes.exitId ? childRes : { entryId: null, exitId: null, exitHandle: 'default' };
  }

  // 3. End sentinel (if present) — chain from the last non-terminated exit.
  if (laid.end) {
    const endRes = emitLaidNode(laid.end, wrapperAbs, ctx);
    linkChain(ctx, chainExit, endRes);
  }

  return { nodes: ctx.nodes, edges: ctx.edges };
}

// ───────────────────────────────────────────────────────────────────
//  Recursive emit dispatch
// ───────────────────────────────────────────────────────────────────

/**
 * Emit a single laid-out IR node (and its subtree) into `ctx.nodes` and
 * `ctx.edges`. Returns the entry/exit info needed to chain-link this
 * subtree into its parent's sequence.
 *
 * @param {object} node                 A laid-out IR node (has `.bbox`)
 * @param {{x:number,y:number}} parentAbs  Parent's absolute origin
 * @param {{nodes:object[],edges:object[],emittedIds:Set<string>}} ctx
 * @returns {{ entryId: string|null, exitId: string|null, exitHandle: string }}
 */
function emitLaidNode(node, parentAbs, ctx) {
  if (!node || !node.bbox) {
    throw new Error(`irToFlat: node missing bbox (kind=${node && node.kind}, id=${node && node.id})`);
  }
  switch (node.kind) {
    case IR_KIND.LEAF:          return emitLeaf(node, parentAbs, ctx);
    case IR_KIND.TERMINATE:     return emitTerminate(node, parentAbs, ctx);
    case IR_KIND.START:
    case IR_KIND.END:           return emitSentinel(node, parentAbs, ctx);
    case IR_KIND.SUB_WORKFLOW:  return emitSubWorkflow(node, parentAbs, ctx);
    case IR_KIND.SWITCH:        return emitSwitch(node, parentAbs, ctx);
    case IR_KIND.FORK:
    case IR_KIND.DYNAMIC_FORK:  return emitFork(node, parentAbs, ctx);
    case IR_KIND.DO_WHILE:      return emitDoWhile(node, parentAbs, ctx);
    default:
      throw new Error(`irToFlat: unknown IR kind '${node.kind}' on id '${node.id}'`);
  }
}

// ───────────────────────────────────────────────────────────────────
//  Scalars (Leaf / Terminate / Start / End / SubWorkflow)
// ───────────────────────────────────────────────────────────────────

function emitLeaf(node, parentAbs, ctx) {
  const abs = add(parentAbs, node.bbox);
  pushNode(ctx, {
    id: node.id,
    type: node.canvasType,
    label: node.label,
    x: abs.x,
    y: abs.y,
    w: node.bbox.w,
    h: node.bbox.h,
    config: node.props || {},
  });
  return { entryId: node.id, exitId: node.id, exitHandle: 'default' };
}

function emitTerminate(node, parentAbs, ctx) {
  const abs = add(parentAbs, node.bbox);
  pushNode(ctx, {
    id: node.id,
    type: CANVAS_TYPE.TERMINATE,
    label: node.label,
    x: abs.x,
    y: abs.y,
    w: node.bbox.w,
    h: node.bbox.h,
    config: { reason: node.reason, status: node.status },
  });
  // Dead-end: chain breaks here.
  return { entryId: node.id, exitId: null, exitHandle: 'default' };
}

function emitSentinel(node, parentAbs, ctx) {
  const abs = add(parentAbs, node.bbox);
  pushNode(ctx, {
    id: node.id,
    type: node.kind === IR_KIND.START ? CANVAS_TYPE.START : CANVAS_TYPE.END,
    label: node.kind === IR_KIND.START ? 'Start' : 'End',
    x: abs.x,
    y: abs.y,
    w: node.bbox.w,
    h: node.bbox.h,
    config: {},
  });
  return { entryId: node.id, exitId: node.id, exitHandle: 'default' };
}

function emitSubWorkflow(node, parentAbs, ctx) {
  const abs = add(parentAbs, node.bbox);
  const flat = {
    id: node.id,
    type: CANVAS_TYPE.SUB_WORKFLOW,
    label: node.label,
    x: abs.x,
    y: abs.y,
    w: node.bbox.w,
    h: node.bbox.h,
    config: {
      subWorkflowName: node.refName,
      ...(node.refVersion != null ? { subWorkflowVersion: node.refVersion } : {}),
      async: !!node.async,
    },
  };
  // Container chrome for SUB_WORKFLOW only makes sense when its body is
  // expanded inline (embedded IR tree present). Today SUB_WORKFLOW leaves
  // embedded=null at design time so we skip the chrome to avoid drawing a
  // 350×130 tinted rect around what is effectively a leaf card. When #119
  // (inline expand) ships, layoutIR will size the bbox to include the
  // embedded subtree and this call will produce a purple-tinted frame
  // automatically.
  if (node.embedded) {
    stampContainerChrome(flat, parentAbs, node);
  }
  pushNode(ctx, flat);
  return { entryId: node.id, exitId: node.id, exitHandle: 'default' };
}

// ───────────────────────────────────────────────────────────────────
//  Switch — head + cases + synthetic convergence pill
// ───────────────────────────────────────────────────────────────────

function emitSwitch(node, parentAbs, ctx) {
  // 1. Emit the head (positioned at headBBox inside the switch's bbox).
  const switchAbs = add(parentAbs, node.bbox);
  const headAbs = {
    x: switchAbs.x + node.headBBox.x,
    y: switchAbs.y + node.headBBox.y,
  };
  const headFlat = {
    id: node.id,
    type: CANVAS_TYPE.CONDITION,
    label: node.label,
    x: headAbs.x,
    y: headAbs.y,
    w: node.headBBox.w,
    h: node.headBBox.h,
    config: { expression: node.expr },
  };
  // Container chrome (#141 / R3-H): wrap head + every case member + the
  // synthesized convergence pill in a tinted rounded rectangle. CanvasPage's
  // renderer (line ~5284) keys on these three properties; leaving them unset
  // silently loses the chrome. `bbox` is the FULL switch subtree bbox in
  // switchAbs' parent-local space — adding parentAbs lifts it into canvas-
  // absolute space, matching the legacy `applyContainerSizing` contract.
  stampContainerChrome(headFlat, parentAbs, node);
  pushNode(ctx, headFlat);

  // 2. Synthesize a convergence pill at the bottom of the switch subtree,
  //    horizontally aligned with the switch trunk.
  const convId = `${node.id}__conv`;
  const convW = node.headBBox.w;
  const convH = 40;
  const convAbs = {
    x: switchAbs.x + node.localTrunkX - convW / 2,
    y: switchAbs.y + node.bbox.h - convH,
  };
  pushNode(ctx, {
    id: convId,
    type: CANVAS_TYPE.CONVERGENCE,
    label: '',
    x: convAbs.x,
    y: convAbs.y,
    w: convW,
    h: convH,
    config: { switchId: node.id },
  });

  // 3. Walk each case + default. Emit each case's children as a sequence,
  //    wire switch→first-child with sourceHandle=case-name, last-child→conv.
  const allCases = [...node.cases];
  if (node.defaultCase) allCases.push(node.defaultCase);

  for (const caseObj of allCases) {
    // caseObj.bbox is in the switch's local space; translate to absolute.
    const caseAbs = add(switchAbs, caseObj.bbox);
    const seqRes = emitSequence(caseObj.children, caseAbs, ctx);

    if (seqRes.entryId == null) {
      // Empty case → ghost edge switch→conv
      pushEdge(ctx, {
        source: node.id,
        target: convId,
        sourceHandle: caseObj.name,
        style: 'dashed',
        isGhost: true,
      });
    } else {
      // switch → first child of case (with handle = case name)
      pushEdge(ctx, {
        source: node.id,
        target: seqRes.entryId,
        sourceHandle: caseObj.name,
      });
      // last reachable child of case → conv (if the case doesn't dead-end)
      if (seqRes.exitId != null) {
        pushEdge(ctx, {
          source: seqRes.exitId,
          target: convId,
          sourceHandle: seqRes.exitHandle,
        });
      }
    }
  }

  return { entryId: node.id, exitId: convId, exitHandle: 'default' };
}

// ───────────────────────────────────────────────────────────────────
//  Fork / DynamicFork — head + branches + join
// ───────────────────────────────────────────────────────────────────

function emitFork(node, parentAbs, ctx) {
  const forkAbs = add(parentAbs, node.bbox);

  // 1. Head
  const headAbs = {
    x: forkAbs.x + node.headBBox.x,
    y: forkAbs.y + node.headBBox.y,
  };
  const isDynamic = node.kind === IR_KIND.DYNAMIC_FORK;
  const headFlat = {
    id: node.id,
    type: CANVAS_TYPE.PARALLEL_FORK,
    label: node.label,
    x: headAbs.x,
    y: headAbs.y,
    w: node.headBBox.w,
    h: node.headBBox.h,
    config: isDynamic ? { isDynamic: true } : {},
  };
  // Container chrome (#141 / R3-H): blue-100 tint for static fork,
  // indigo-100 for dynamic fork. Wraps head + every branch member + join.
  stampContainerChrome(headFlat, parentAbs, node);
  pushNode(ctx, headFlat);

  // 2. Join (always present per IR schema; layoutFork may leave it null for
  //    zero-branch dynamic forks where we treat the head alone as the whole
  //    footprint, but the IR node itself still carries .join).
  let joinId = null;
  if (node.join && node.join.bbox) {
    joinId = node.join.id;
    const joinAbs = {
      x: forkAbs.x + node.join.bbox.x,
      y: forkAbs.y + node.join.bbox.y,
    };
    pushNode(ctx, {
      id: joinId,
      type: CANVAS_TYPE.PARALLEL_JOIN,
      label: node.join.label,
      x: joinAbs.x,
      y: joinAbs.y,
      w: node.join.bbox.w,
      h: node.join.bbox.h,
      config: {
        joinOn: [...(node.join.joinOn || [])],
        joinMode: node.join.waitMode === 'ANY' ? 'ANY_COMPLETED' : 'ALL_COMPLETED',
      },
    });
  }

  // 3. Walk each branch.
  const branches = isDynamic ? (node.resolvedBranches || []) : node.branches;
  for (const branch of branches) {
    const branchAbs = add(forkAbs, branch.bbox);
    const seqRes = emitSequence(branch.children, branchAbs, ctx);

    if (seqRes.entryId == null) {
      // Empty branch → ghost edge fork→join
      if (joinId) {
        pushEdge(ctx, {
          source: node.id,
          target: joinId,
          sourceHandle: branch.name,
          style: 'dashed',
          isGhost: true,
        });
      }
    } else {
      // fork → first child (with branch handle)
      pushEdge(ctx, {
        source: node.id,
        target: seqRes.entryId,
        sourceHandle: branch.name,
      });
      // last child → join
      if (joinId && seqRes.exitId != null) {
        pushEdge(ctx, {
          source: seqRes.exitId,
          target: joinId,
          sourceHandle: seqRes.exitHandle,
        });
      }
    }
  }

  // Fork subtree exit = the join (if present). If no join, the fork head
  // itself is the exit (DynamicFork zero-branch case).
  return {
    entryId: node.id,
    exitId: joinId || node.id,
    exitHandle: 'default',
  };
}

// ───────────────────────────────────────────────────────────────────
//  DoWhile — head + body chain + loop_back + exit
// ───────────────────────────────────────────────────────────────────

function emitDoWhile(node, parentAbs, ctx) {
  const loopAbs = add(parentAbs, node.bbox);

  // 1. Head
  const headAbs = {
    x: loopAbs.x + node.headBBox.x,
    y: loopAbs.y + node.headBBox.y,
  };
  const headFlat = {
    id: node.id,
    type: CANVAS_TYPE.LOOP,
    label: node.label,
    x: headAbs.x,
    y: headAbs.y,
    w: node.headBBox.w,
    h: node.headBBox.h,
    config: {
      loopCondition: node.condition,
      ...(node.iterationHint ? { iterationHint: node.iterationHint } : {}),
    },
  };
  // Container chrome (#122 / R3-H): blue-grey tint wraps head + body frame.
  // Matches the DO_WHILE frame originally shipped by applyDoWhileContainerSizing.
  stampContainerChrome(headFlat, parentAbs, node);
  pushNode(ctx, headFlat);

  // 2. Body: walk as a sequence. Body children's bboxes are in the loop
  //    subtree's local space (laid into the frame by layoutDoWhile), so we
  //    translate relative to loopAbs.
  const bodySeq = emitSequence(node.body, loopAbs, ctx);

  // 3. Wire: head → first body child (default). Last body child → head
  //    (loop_back).
  if (bodySeq.entryId != null) {
    pushEdge(ctx, {
      source: node.id,
      target: bodySeq.entryId,
      sourceHandle: 'default',
    });
    if (bodySeq.exitId != null) {
      pushEdge(ctx, {
        source: bodySeq.exitId,
        target: node.id,
        sourceHandle: 'loop_back',
      });
    }
  }

  // 4. The loop's exit port is 'exit' — the outer sequence walker will
  //    emit the outgoing edge from `node.id` with sourceHandle='exit'.
  return { entryId: node.id, exitId: node.id, exitHandle: 'exit' };
}

// ───────────────────────────────────────────────────────────────────
//  Sequence walker (chain children)
// ───────────────────────────────────────────────────────────────────

/**
 * Walk a child-array sequence, emitting each child and chaining them with
 * default edges. Returns the first-entry / last-reachable-exit as a triple
 * suitable for linking into an enclosing flow.
 *
 * @param {object[]} children
 * @param {{x:number,y:number}} parentAbs  Origin of the container whose
 *                                         local space the children's bboxes
 *                                         are in
 * @param {object} ctx
 * @returns {{ entryId: string|null, exitId: string|null, exitHandle: string }}
 */
function emitSequence(children, parentAbs, ctx) {
  if (!children || children.length === 0) {
    return { entryId: null, exitId: null, exitHandle: 'default' };
  }
  let entryId = null;
  let chainExit = { entryId: null, exitId: null, exitHandle: 'default' };
  for (const child of children) {
    const res = emitLaidNode(child, parentAbs, ctx);
    if (entryId == null) entryId = res.entryId;
    linkChain(ctx, chainExit, res);
    // If this child is a dead-end (Terminate), downstream children in the
    // sequence can't be reached via default edges; we still emit them as
    // free-standing flat nodes (layout placed them), but the chain exit
    // becomes null so no further default edge is synthesized from them
    // on behalf of this sequence.
    chainExit = res.exitId ? res : { entryId: null, exitId: null, exitHandle: 'default' };
  }
  return { entryId, exitId: chainExit.exitId, exitHandle: chainExit.exitHandle };
}

/**
 * Emit a default edge from `prev.exitId` to `curr.entryId` with
 * `sourceHandle = prev.exitHandle`. No-op if either side has a null exit/
 * entry (e.g. after a Terminate, or before the first real node).
 */
function linkChain(ctx, prev, curr) {
  if (!prev || prev.exitId == null) return;
  if (!curr || curr.entryId == null) return;
  pushEdge(ctx, {
    source: prev.exitId,
    target: curr.entryId,
    sourceHandle: prev.exitHandle || 'default',
  });
}

// ───────────────────────────────────────────────────────────────────
//  Container chrome (R3-H — task #158)
// ───────────────────────────────────────────────────────────────────

/**
 * Stamp container-chrome fields onto a container's flat head node so
 * CanvasPage's renderer draws the tinted rounded-rect background (see
 * CanvasPage.jsx §"Container chrome"). Mirrors the contract the legacy
 * `applyContainerSizing` pass in utils/dagLayout.js publishes for the
 * Sugiyama path, so both layouts produce pixel-compatible containers.
 *
 * - `_containerBounds` is the FULL subtree bbox in canvas-absolute
 *   coordinates. The IR stores container bboxes in their parent's local
 *   space, so we lift to absolute via `add(parentAbs, node.bbox)`.
 * - `_containerKind` matches the IR_KIND string for that container —
 *   the chrome code maps the same strings to per-kind tint colours.
 * - `_containerChildren` is a flat array of every descendant id (post-
 *   layout convention #141), useful for downstream consumers such as
 *   the execution-view overlay that wants to enumerate runtime children.
 *
 * No-op if `node.bbox` is missing (defensive: layoutIR always populates
 * it, but a hypothetical unlaid caller shouldn't crash here — the
 * chrome simply doesn't render).
 *
 * @param {object} flatNode   The container's flat head node (mutated in place)
 * @param {{x:number,y:number}} parentAbs  Absolute origin of the container's parent
 * @param {object} irNode     The IR container node (has `kind`, `bbox`)
 */
function stampContainerChrome(flatNode, parentAbs, irNode) {
  if (!irNode || !irNode.bbox) return;
  const abs = add(parentAbs, irNode.bbox);
  flatNode._containerBounds = {
    x: abs.x,
    y: abs.y,
    width: irNode.bbox.w,
    height: irNode.bbox.h,
  };
  flatNode._containerKind = irNode.kind; // 'switch' | 'fork' | 'dynamicFork' | 'doWhile' | 'subWorkflow'
  flatNode._containerChildren = collectContainerDescendantIds(irNode);
}

/**
 * Walk an IR container's children and return a flat array of descendant
 * ids (NOT including the container itself, NOT including its synthesized
 * convergence pill — only real IR nodes). Mirrors the post-layout shape of
 * `_containerChildren` in the legacy pipeline (`applyContainerSizing` in
 * dagLayout.js overwrites the structured wire hint with exactly this list).
 *
 * @param {object} node  IR container node
 * @returns {string[]}   Descendant ids in traversal order
 */
function collectContainerDescendantIds(node) {
  const out = [];
  const walk = (n, isRoot) => {
    if (!n) return;
    if (!isRoot && typeof n.id === 'string') out.push(n.id);
    switch (n.kind) {
      case IR_KIND.SWITCH: {
        for (const c of n.cases || []) {
          for (const ch of c.children || []) walk(ch, false);
        }
        if (n.defaultCase) {
          for (const ch of n.defaultCase.children || []) walk(ch, false);
        }
        break;
      }
      case IR_KIND.FORK:
      case IR_KIND.DYNAMIC_FORK: {
        const branches = n.kind === IR_KIND.DYNAMIC_FORK
          ? (n.resolvedBranches || [])
          : (n.branches || []);
        for (const b of branches) {
          for (const ch of b.children || []) walk(ch, false);
        }
        // Every fork/dynamicFork owns its join — always include it as a
        // descendant (the join lives inside the container frame, sits at
        // the bottom of the tinted rect).
        if (n.join && typeof n.join.id === 'string') out.push(n.join.id);
        break;
      }
      case IR_KIND.DO_WHILE: {
        for (const ch of n.body || []) walk(ch, false);
        break;
      }
      case IR_KIND.SUB_WORKFLOW: {
        if (n.embedded) walk(n.embedded, false);
        break;
      }
      default:
        break;
    }
  };
  walk(node, /*isRoot*/ true);
  return out;
}

// ───────────────────────────────────────────────────────────────────
//  Ctx helpers
// ───────────────────────────────────────────────────────────────────

function pushNode(ctx, flatNode) {
  if (ctx.emittedIds.has(flatNode.id)) {
    throw new Error(`irToFlat: duplicate node id '${flatNode.id}'`);
  }
  ctx.emittedIds.add(flatNode.id);
  ctx.nodes.push(flatNode);
}

function pushEdge(ctx, edge) {
  ctx.edges.push(edge);
}

function add(parentAbs, bbox) {
  return { x: parentAbs.x + bbox.x, y: parentAbs.y + bbox.y };
}

// Silence unused-import warnings for the bridge helper we don't use
// directly (we branch on kind + CANVAS_TYPE instead).
/* eslint-disable no-unused-vars */
const _unused = { irKindToCanvasType };
/* eslint-enable no-unused-vars */
